import { useNavigate, useParams } from "react-router-dom";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import { useEffect, useState } from "react";
import api from "../api";
import Daily from "@daily-co/daily-js";
import usePrompt from "../common/hooks/usePrompt";
import useBeforeUnload from "../common/hooks/useBeforeUnload";
import Room from "../components/Room";
import styles from "./LiveLesson.module.css";
import { formatDateV2, formatSeconds, formatTimeV2, UTCtoLocal } from "../utils";
import { DailyProvider } from "@daily-co/daily-react";
import { LessonState } from "../common/constants";
import Loader from "../components/Loader";
import typo from "../typography.module.css"
import Button from "../components/Button";
import { PiPProvider } from "../common/PiPContext";

const LiveLesson = () => {

    const { roomName } = useParams();
    const navigate = useNavigate()
    const [lesson, setLesson] = useState(null)
    const [call, setCall] = useState(null)
    const [token, setToken] = useState(null)
    const [startInterval, setStartInterval] = useState(null)
    const [remainingTime, setRemainingTime] = useState(null)
    const [lessonState, setLessonState] = useState(null)
    const [roomError, setRoomError] = useState(null)
    const [meetingState, setMeetingState] = useState(null)

    const [testCompleted, setTestCompleted] = useState(false)

    usePrompt('Sei sicuro di voler abbandonare la lezione ?', !!call && !['error', 'left-meeting'].includes(meetingState))
    useBeforeUnload('Sei sicuro di voler abbandonare la lezione ?', !!call && !['error', 'left-meeting'].includes(meetingState))
    useEffect(() => {
        document.body.style['overscroll-behavior-x'] = 'none';
    }, [])

    useEffect(() => {
        const getLesson = async (roomName) => {
            try {
                const lesson = await api.get(`/admin/lessons/${roomName}`);
                setLesson(lesson);
            } catch (e) {
                console.error(e);
            }
        }

        if (roomName) {
            getLesson(roomName);
        }
    }, [roomName])

    useEffect(() => {
        if (!lesson) return;

        const interval = setInterval(() => {
            getRemainingTime()
        }, 1000)

        setStartInterval(interval)

        const getLessonMaterial = async (lessonId) => {
            try {
                const materials = await api.get(`/admin/lessons/${lessonId}/materials`);

                setLesson((prev) => {
                    prev.materials = materials;
                    return { ...prev };
                });
            } catch (e) {
                console.error(e);
            }
        };

        const getModuleMaterial = async (moduleId) => {
            try {
                const materials = await api.get(`/admin/modules/${moduleId}/materials`);

                setLesson((prev) => {
                    prev.module_materials = materials.filter(material => !prev.materials?.some(existing => existing.id === material.id));
                    return { ...prev };
                });
            } catch (e) {
                console.error(e);
            }
        }

        if (!lesson.materials) {
            getLessonMaterial(lesson.id);
            getModuleMaterial(lesson.module.id);
        }

        return () => {
            clearInterval(interval);
        };
    }, [lesson])

    useEffect(() => {
        if (lesson && remainingTime) {
            if (lesson.ended_at) {
                setLessonState(LessonState.Ended)
                clearInterval(startInterval)
                setStartInterval(null)
            }
            else if (remainingTime < 7200) {
                setLessonState(LessonState.Live)
                clearInterval(startInterval)
                setStartInterval(null)
            }
            else {
                setLessonState(LessonState.TooEarly)
            }
        }

    }, [remainingTime, lesson])

    useEffect(() => {
        if (lessonState === LessonState.Live) {
            getMeetingToken()
        }
    }, [lessonState])

    useEffect(() => {
        if (token !== null) {
            startCall()
        }
    }, [token])

    useEffect(() => {
        if (call) {
            call.on('error', (e) => {
                setRoomError(e)
                setMeetingState('error')
            })
            call.on('left-meeting', (e) => {
                setRoomError(e)
                setMeetingState('left-meeting')
            })
        }

        return () => {
            if (call) {
                call.leave();
                call.destroy();
            }
        };
    }, [call])

    const getRemainingTime = () => {
        const now = new Date().getTime();
        const start = UTCtoLocal(lesson.starts_at).getTime();
        setRemainingTime(parseInt((start - now) / 1000))
        return parseInt((start - now) / 1000);
    }

    const startCall = async () => {
        try {
            if (!call) {
                const call = Daily.createCallObject({ url: `https://startingfinance.daily.co/${lesson.room_name}`, token: token ?? '' })
                setCall(call)
                call.join()
            }
            else {
                call.join()
            }
        }
        catch (e) {
            console.error(e)
            setRoomError(e)
        }
    }

    const getMeetingToken = async () => {
        try {
            let token = await api.get(`/admin/lessons/${lesson.id}/token`)
            setToken(token.token ?? '')
        }
        catch (e) {
            console.error(e)
        }
    }

    return (
        <PiPProvider>
            <HeaderFooterLayout hideHeader={!lessonState || lessonState === LessonState.Live} hideFooter={!lessonState || lessonState === LessonState.Live}>
                {lessonState === LessonState.Live && call &&
                    <DailyProvider callObject={call}>
                        <Room lesson={lesson} />
                    </DailyProvider>

                }
                {lessonState === LessonState.TooEarly &&
                    <div className={styles.container} style={{ gap: '1rem', padding: '1rem', textAlign: 'center' }}>
                        <div className={typo.subtitle}>Troppo presto...</div>
                        <div className={typo.headline}>La lezione inizia il {formatDateV2(lesson.starts_at, {day: 'numeric', month: 'long'})} alle {formatTimeV2(lesson.starts_at)} e sarà accessibile da due ore prima.</div>
                        <Button
                            style={{ fontSize: '1.125rem', padding: '1rem 3rem' }}
                            onClick={() => {
                                window.location.href = '/';
                            }}>TORNA ALLA HOME</Button>
                    </div>
                }
                {lessonState === LessonState.Ended &&
                    <div className={styles.container} style={{ gap: '1rem', padding: '1rem', textAlign: 'center' }}>
                        <div className={typo.subtitle}>Lezione terminata</div>
                        <div className={typo.headline}>La lezione è terminata il {formatDateV2(lesson.ended_at ?? lesson.ends_at, { day: 'numeric', month: 'long', year: 'numeric' }, 'it', true, true)} alle {formatTimeV2(lesson.ended_at ?? lesson.ends_at)}, la registrazione sarà disponibile a breve.</div>
                        <Button
                            style={{ fontSize: '1.125rem', padding: '1rem 3rem' }}
                            onClick={() => {
                                window.location.href = '/';
                            }}>TORNA ALLA HOME</Button>
                    </div>
                }
                {lessonState === null &&
                    <div className={styles.container}>
                        <Loader />
                        <div className={typo.headline}>
                            Caricamento...
                        </div>
                    </div>
                }
            </HeaderFooterLayout>
        </PiPProvider>
    )

}

export default LiveLesson;